import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { LazyLoadEvent } from 'primeng/api';
import { MessageService } from 'primeng/api';
import { SelectItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ObjectQueryFilter } from '@tj/tj-dmp-lib-api';
import {
  ManualAcquisitionCostService,
  ManualAcquisitionBulkJobStatus,
  ManualAcquisitionPreviewResponse,
  ManualAcquisitionPreviewRow,
  ManualAcquisitionScope
} from '../../../../services/manual-acquisition-cost.service';

export interface ManualAcquisitionBulkDialogData {
  tenant: string;
  partner: string;
  dataviewId: string;
  queryFilter: ObjectQueryFilter;
  scope: ManualAcquisitionScope;
  /** Same as liste view: rows per page for preview table */
  initialPageSize?: number;
  /** True when opened from expanded row: filter is list + this row only */
  singleRowMode?: boolean;
}

@Component({
  selector: 'dmp-manual-acquisition-bulk-dialog',
  templateUrl: './manual-acquisition-bulk-dialog.component.html',
  styleUrls: ['./manual-acquisition-bulk-dialog.component.scss'],
  standalone: false
})
export class ManualAcquisitionBulkDialogComponent implements OnInit, OnDestroy {
  tenant: string;
  partner: string;
  dataviewId: string;
  queryFilter: ObjectQueryFilter;
  scope: ManualAcquisitionScope;
  singleRowMode = false;

  rowByPages: SelectItem[] = [
    { value: 10 },
    { value: 20 },
    { value: 30 },
    { value: 40 },
    { value: 50 },
    { value: 100 },
    { value: 200 },
    { value: 500 },
    { value: 1000 }
  ];
  selectedRowByPage: SelectItem;

  rows: ManualAcquisitionPreviewRow[] = [];
  totalRecords = 0;
  first = 0;
  pageSize = 20;
  loading = false;

  amount: number | null = null;
  selectedRows: ManualAcquisitionPreviewRow[] = [];
  /** Éligible main-row ids explicitly unchecked in the preview (sent to API as deselectedMainRowIds). */
  deselectedMainRowIds = new Set<number>();

  applying = false;
  jobId: string | null = null;
  jobStatus: ManualAcquisitionBulkJobStatus | null = null;
  jobFinished = false;

  private pollSub: Subscription | null = null;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private manualAcquisitionCostService: ManualAcquisitionCostService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const d = this.config.data as ManualAcquisitionBulkDialogData;
    this.tenant = d.tenant;
    this.partner = d.partner;
    this.dataviewId = d.dataviewId;
    this.queryFilter = d.queryFilter || {};
    this.scope = d.scope;
    this.singleRowMode = !!d.singleRowMode;
    const want = d.initialPageSize ?? 20;
    this.selectedRowByPage =
      this.rowByPages.find((r) => r.value === want) || this.rowByPages.find((r) => r.value === 20) || this.rowByPages[0];
    this.pageSize = this.selectedRowByPage.value as number;
    this.deselectedMainRowIds.clear();
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  /** Eligible rows on the currently loaded preview page only */
  get eligibleRowsOnPage(): ManualAcquisitionPreviewRow[] {
    return this.rows.filter((r) => r.eligible);
  }

  /** Header “select all on this page”: checked when every eligible row on this page is selected */
  get allEligibleOnPageSelected(): boolean {
    const elig = this.eligibleRowsOnPage;
    if (elig.length === 0) {
      return false;
    }
    return elig.every((row) => this.isRowSelected(row));
  }

  /** Même règle que le garde dans {@link apply} : pas d’Appliquer sans montant exploitable. */
  get isApplyButtonDisabled(): boolean {
    if (this.loading || this.applying) {
      return true;
    }
    const a = this.amount;
    return a === null || a === undefined || Number.isNaN(Number(a));
  }

  isRowSelected(row: ManualAcquisitionPreviewRow): boolean {
    const k = this.rowKey(row);
    return this.selectedRows.some((s) => this.rowKey(s) === k);
  }

  private rowKey(row: ManualAcquisitionPreviewRow): string {
    return this.scope === 'PERSONAL' ? `p-${row.personalId}` : `c-${row.commandId ?? 0}`;
  }

  onHeaderCheckboxChange(checked: boolean): void {
    const elig = this.eligibleRowsOnPage;
    if (elig.length === 0) {
      return;
    }
    if (checked) {
      this.selectedRows = [...elig];
    } else {
      this.selectedRows = [];
    }
    this.syncDeselectedFromTableSelection();
  }

  /** Après sélection/désélection via cases lignes (PrimeNG). */
  onTableSelectionChanged(): void {
    this.syncDeselectedFromTableSelection();
  }

  private syncDeselectedFromTableSelection(): void {
    for (const r of this.eligibleRowsOnPage) {
      if (r.mainRowId <= 0) {
        continue;
      }
      if (this.isRowSelected(r)) {
        this.deselectedMainRowIds.delete(r.mainRowId);
      } else {
        this.deselectedMainRowIds.add(r.mainRowId);
      }
    }
  }

  onPageSizeChange(): void {
    this.pageSize = this.selectedRowByPage.value as number;
    this.first = 0;
    this.triggerPreviewReload();
  }

  private triggerPreviewReload(): void {
    this.loading = true;
    const page = 0;
    this.manualAcquisitionCostService
      .preview(this.tenant, this.partner, this.dataviewId, page, this.pageSize, this.scope, this.queryFilter)
      .subscribe({
        next: (res) => this.applyPreviewResult(res),
        error: (err) => this.handlePreviewError(err)
      });
  }

  onLazyLoad(event: LazyLoadEvent): void {
    this.first = event.first ?? 0;
    this.pageSize = event.rows ?? this.pageSize;
    this.loading = true;
    const page = Math.floor(this.first / this.pageSize);
    this.manualAcquisitionCostService
      .preview(this.tenant, this.partner, this.dataviewId, page, this.pageSize, this.scope, this.queryFilter)
      .subscribe({
        next: (res) => this.applyPreviewResult(res),
        error: (err) => this.handlePreviewError(err)
      });
  }

  private applyPreviewResult(res: ManualAcquisitionPreviewResponse): void {
    const list = res.rows || [];
    const mapped = list.map((r) => ({
      ...r,
      mainRowId: r.mainRowId ?? 0,
      _key: this.scope === 'PERSONAL' ? `p-${r.personalId}` : `c-${r.commandId ?? 0}`
    })) as (ManualAcquisitionPreviewRow & { _key: string })[];
    this.rows = mapped;
    this.totalRecords = res.totalElements;
    this.selectedRows = mapped.filter(
      (r) => r.eligible && r.mainRowId > 0 && !this.deselectedMainRowIds.has(r.mainRowId)
    );
    this.loading = false;
  }

  private handlePreviewError(err: unknown): void {
    this.loading = false;
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('manualAcquisition.bulk.errorPreview'),
      detail: (err as { error?: { detail?: string }; message?: string })?.error?.detail ||
        (err as { message?: string })?.message ||
        String(err)
    });
  }

  rowRowClass(row: ManualAcquisitionPreviewRow): string {
    if (!row.eligible) {
      return 'row-blocked';
    }
    return '';
  }

  displayAcquisition(row: ManualAcquisitionPreviewRow): number | null {
    if (this.scope === 'PERSONAL') {
      if (row.campaignCostContact != null) {
        return row.campaignCostContact;
      }
      return row.currentAmount ?? null;
    }
    if (row.campaignCostOrder != null) {
      return row.campaignCostOrder;
    }
    return row.currentAmount ?? null;
  }

  acquisitionColumnHeaderKey(): string {
    return this.scope === 'PERSONAL'
      ? 'manualAcquisition.displayAcquisitionContact'
      : 'manualAcquisition.displayAcquisitionOrder';
  }

  /** PrimeNG replaces `{totalRecords}` and `{totalPages}` in the paginator report. */
  get paginatorReportTemplate(): string {
    return this.translate.instant('manualAcquisition.bulk.paginatorReport');
  }

  progressMode(): string {
    if (!this.jobStatus || !(this.jobStatus.totalRows > 0)) {
      return 'indeterminate';
    }
    return 'determinate';
  }

  progressValue(): number {
    if (!this.jobStatus || this.jobStatus.totalRows <= 0) {
      return 0;
    }
    const cur = Math.min(this.jobStatus.processedRows, this.jobStatus.totalRows);
    return Math.min(100, Math.round((100 * cur) / this.jobStatus.totalRows));
  }

  /** Avoid showing e.g. 847 / 1 if the backend over-counts processed rows */
  progressDetailCurrent(): number {
    if (!this.jobStatus || this.jobStatus.totalRows <= 0) {
      return 0;
    }
    return Math.min(this.jobStatus.processedRows, this.jobStatus.totalRows);
  }

  /** Safe numeric display for optional API fields */
  nz(v: number | undefined | null): number {
    return v ?? 0;
  }

  apply(): void {
    if (this.amount === null || this.amount === undefined || Number.isNaN(Number(this.amount))) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('manualAcquisition.bulk.amountRequired')
      });
      return;
    }
    this.applying = true;
    this.jobFinished = false;
    this.jobStatus = null;
    this.jobId = null;
    this.pollSub?.unsubscribe();

    const deselectedPayload = Array.from(this.deselectedMainRowIds).filter((id) => id > 0);

    this.manualAcquisitionCostService
      .startBulkApplyByFilter(
        this.tenant,
        this.partner,
        this.dataviewId,
        this.scope,
        Number(this.amount),
        this.queryFilter,
        deselectedPayload
      )
      .subscribe({
        next: (res) => {
          this.jobId = res.jobId;
          this.startPolling();
        },
        error: (err) => {
          this.applying = false;
          this.jobFinished = true;
          this.jobStatus = {
            jobId: '',
            status: 'FAILED',
            totalRows: 0,
            processedRows: 0,
            updatedCount: 0,
            skippedCount: 0,
            errorMessage:
              (err as { error?: { detail?: string }; message?: string })?.error?.detail ||
              (err as { message?: string })?.message ||
              String(err)
          };
        }
      });
  }

  private startPolling(): void {
    if (!this.jobId) {
      return;
    }
    /* RxJS 6: takeWhile(..., inclusive) is unavailable — stop the timer on terminal status ourselves */
    this.pollSub = timer(0, 1500)
      .pipe(switchMap(() => this.manualAcquisitionCostService.getBulkJob(this.tenant, this.jobId!)))
      .subscribe({
        next: (s) => {
          this.jobStatus = s;
          if (s.status === 'COMPLETED' || s.status === 'FAILED') {
            this.applying = false;
            this.jobFinished = true;
            this.pollSub?.unsubscribe();
            this.pollSub = null;
          }
        },
        error: (err) => {
          this.applying = false;
          this.jobFinished = true;
          this.pollSub?.unsubscribe();
          this.pollSub = null;
          this.jobStatus = {
            jobId: this.jobId || '',
            status: 'FAILED',
            totalRows: 0,
            processedRows: 0,
            updatedCount: 0,
            skippedCount: 0,
            errorMessage:
              (err as { error?: { detail?: string }; message?: string })?.error?.detail ||
              (err as { message?: string })?.message ||
              String(err)
          };
        }
      });
  }

  closeAfterResult(): void {
    const ok = this.jobStatus?.status === 'COMPLETED';
    this.ref.close(ok);
  }

  /** After a failed job (or start failure): show preview again, keep filters, allow changing amount and re-applying. */
  retryAfterError(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.jobFinished = false;
    this.jobStatus = null;
    this.jobId = null;
    this.applying = false;
    this.reloadPreviewCurrentPage();
  }

  private reloadPreviewCurrentPage(): void {
    this.loading = true;
    const page = Math.floor(this.first / this.pageSize);
    this.manualAcquisitionCostService
      .preview(this.tenant, this.partner, this.dataviewId, page, this.pageSize, this.scope, this.queryFilter)
      .subscribe({
        next: (res) => this.applyPreviewResult(res),
        error: (err) => this.handlePreviewError(err)
      });
  }

  cancel(): void {
    if (this.applying) {
      return;
    }
    this.ref.close(false);
  }
}
